import { useState } from "react";
import { Output } from "valibot";

import SendButton from "components/ChatPanel/SendButton";
import LoadingSpinner from "components/LoadingSpinner";
import { BotInfoSchema, UserFormInfoSchema } from "components/SonicBot/schemas";
import { defaultFileMaxSize, defaultMaxFiles } from "shared/constants";
import { GenerationMode, Literals } from "shared/types";

import AttachmentsInput from "./Dropper/AttachmentsInput";
import ListOptionInput, { ListOption } from "./ListOption";
import TextAreaInput from "./TextAreaInput";
import TextInput from "./TextInput";
import { handleFilesUpload } from "./functions";

type Props = {
  isIframe: boolean;
  message: string;
  setMessage: (value: React.SetStateAction<string>) => void;
  eventSource: EventSource | null;
  generationMode: GenerationMode;
  setGenerationMode: React.Dispatch<React.SetStateAction<GenerationMode>>;
  inputRef: React.RefObject<any>;
  handleSubmit: ({
    message,
    skipSubmission,
    formFieldValue,
  }: {
    message: string;
    skipSubmission?: boolean;
    formFieldValue?: any;
  }) => void;
  botInfo: Output<typeof BotInfoSchema>;
  formFieldInfo?: Output<typeof UserFormInfoSchema>;
  copyTexts: Literals;
};

function ChatInputBox(props: Props) {
  const {
    isIframe,
    message,
    setMessage,
    eventSource,
    generationMode,
    setGenerationMode,
    inputRef,
    handleSubmit,
    botInfo,
    formFieldInfo,
    copyTexts,
  } = props;

  const [files, setFiles] = useState<File[]>([]);
  const [isUploadingFiles, setIsUploadingFiles] = useState<boolean>(false);

  const inputType = formFieldInfo?.form_field.type || "textarea";
  const placeholder =
    formFieldInfo?.form_field.placeholder ||
    botInfo.bot_info_config.custom_placeholder ||
    "Send a message...";

  const showSendButton =
    !isUploadingFiles &&
    (inputType === "text" ||
      inputType === "email" ||
      inputType === "textarea" ||
      files.length > 0);

  const renderInputs = () => {
    switch (inputType) {
      case "list_option":
        return (
          <ListOptionInput
            options={formFieldInfo?.form_field?.options || []}
            onClick={async (option: ListOption) => {
              handleSubmit({
                message: option.label,
                formFieldValue: option.value,
              });
            }}
          />
        );
      case "attachments":
        return (
          <AttachmentsInput
            selectedFiles={files}
            onChange={(files: File[]) => {
              setMessage(`[Attachments: ${files.map(f => f.name).join(", ")}]`);
              setFiles(files);
            }}
            maxFiles={defaultMaxFiles}
            maxFileSize={
              formFieldInfo?.form_field.max_file_size ?? defaultFileMaxSize
            }
            disabled={isUploadingFiles}
            copyTexts={copyTexts}
          />
        );
      case "text":
      case "email":
        return (
          <div className="px-3 py-2.5">
            <TextInput
              ref={inputRef}
              onChange={(value: string) => setMessage(value)}
              value={message}
              placeholder={placeholder}
              type={inputType}
              maxLength={formFieldInfo?.form_field.max_length}
            />
          </div>
        );

      default:
        return (
          <div className="px-3 py-2.5 pb-1.25">
            <TextAreaInput
              ref={inputRef}
              onChange={(value: string) => setMessage(value)}
              value={message}
              placeholder={placeholder}
              maxLength={formFieldInfo?.form_field.max_length}
              isIframe={isIframe}
              onKeyDown={evt => {
                if (evt.key === "Enter" && !evt.shiftKey && !evt.ctrlKey) {
                  evt.preventDefault();
                  evt.stopPropagation();
                  handleSend();
                }
              }}
            />
          </div>
        );
    }
  };

  const handleSend = async () => {
    if (files.length > 0) {
      try {
        setIsUploadingFiles(true);
        const presignedUrls = await handleFilesUpload(files, botInfo.id);
        setFiles([]);
        handleSubmit({
          message,
          formFieldValue: presignedUrls.map(
            ({ presignedUrl, fileKey }, index) => ({
              url: presignedUrl,
              file_name: files[index].name,
              mimetype: files[index].type,
              file_size: files[index].size,
              file_key: fileKey,
            }),
          ),
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error uploading files", err);
      } finally {
        setIsUploadingFiles(false);
      }
    } else {
      handleSubmit({ message, formFieldValue: message });
    }
  };

  return (
    <form
      onSubmit={evt => {
        evt.preventDefault();
        showSendButton && handleSend();
      }}
    >
      <div className="relative w-full">
        {renderInputs()}

        {showSendButton && (
          <SendButton
            generationMode={generationMode}
            setGenerationMode={setGenerationMode}
            eventSource={eventSource}
            disabled={!message.trim()}
          />
        )}
        {isUploadingFiles && (
          <div className="button-container h-5 w-5 border-white">
            <LoadingSpinner className="border-accent" />
          </div>
        )}
      </div>
      {formFieldInfo?.is_skippable && (
        <div className="flex w-full items-center justify-center bg-white py-2">
          <button
            className="border-none bg-transparent px-3.5 py-1.5 text-sm font-semibold text-negative-weak"
            onClick={() => {
              setFiles([]);
              handleSubmit({
                message: copyTexts.cancelButtonText || "Cancel",
                skipSubmission: true,
              });
            }}
            type="button"
            disabled={isUploadingFiles}
          >
            {copyTexts.cancelButtonText || "Cancel"}
          </button>
        </div>
      )}
    </form>
  );
}

export default ChatInputBox;
